import React, { useEffect, useState } from 'react';
import { FaServer, FaWifi, FaPowerOff, FaBell, FaExclamationCircle } from 'react-icons/fa';
import monitoringApi from '../../../services/monitoringApi';
import RealTimeMonitoring from './RealTimeMonitoring';

// Admin view: cross-farm summary + the full device detail dashboard
// (RealTimeMonitoring lists ALL devices for admins via the scoped API).
export default function AdminMonitoring() {
  const [summary, setSummary] = useState(null);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await monitoringApi.getOverview();
        if (!mounted) return;
        setSummary(res.summary);
        setFarms(res.farms || []);
      } catch (err) {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const cards = summary
    ? [
        { label: 'Total Devices', value: summary.totalDevices, icon: FaServer, color: 'text-primary' },
        { label: 'Online', value: summary.online, icon: FaWifi, color: 'text-green-600' },
        { label: 'Offline', value: summary.offline, icon: FaPowerOff, color: 'text-gray-400' },
        { label: 'Active Alerts', value: summary.activeAlerts, icon: FaBell, color: 'text-amber-600' },
        { label: 'Critical', value: summary.criticalAlerts, icon: FaExclamationCircle, color: 'text-red-600' },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary">Farm Monitoring Overview</h1>
        <p className="text-sm text-gray-500">All farms, devices and live climate reports</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-gray-400">{c.label}</span>
                <Icon className={c.color} />
              </div>
              <div className="text-2xl font-bold text-gray-800 mt-2">
                {loading ? '…' : c.value ?? 0}
              </div>
            </div>
          );
        })}
      </div>

      {/* Farms table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">Farms</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Farmer</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Devices</th>
                <th className="text-left px-4 py-2 font-medium">Online</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {farms.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-400">No farms with devices yet.</td>
                </tr>
              )}
              {farms.map((f) => (
                <tr key={f.farmerId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{f.companyName || f.farmerName || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{f.email || '—'}</td>
                  <td className="px-4 py-2 text-gray-700">{f.deviceCount}</td>
                  <td className="px-4 py-2">
                    <span className={f.onlineCount > 0 ? 'text-green-600' : 'text-gray-400'}>
                      {f.onlineCount}/{f.deviceCount}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full device detail dashboard (all devices, admin-scoped) */}
      <div className="pt-2 border-t border-gray-100">
        <RealTimeMonitoring />
      </div>
    </div>
  );
}
